import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';
import {
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  sectionPaddings,
  standardColours,
} from '../styles';
import { Container, Heading, Overline } from './ui';

const StyledTeamProfiles = styled.section`
  ${sectionPaddings()};
  background-color: ${standardColours.lightGrey};
`;

const StyledHeader = styled.header`
  margin-bottom: 30px;
  text-align: center;

  ${minBreakpointQuery.small`
    margin-bottom: 40px;
  `}

  ${minBreakpointQuery.medium`
    margin-bottom: 50px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 60px;
  `}

  ${minBreakpointQuery.xxlarge`
    margin-bottom: 70px;
  `}

  ${minBreakpointQuery.xxxxlarge`
    margin-bottom: 80px;
  `}
`;

const StyledOverline = styled(Overline)`
  margin-bottom: 16px;

  ${minBreakpointQuery.small`
    margin-bottom: 18px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 20px;
  `}
`;

const StyledItems = styled.div`
  display: grid;
  gap: 30px;

  ${minBreakpointQuery.small`
    gap: 40px;
  `}

  ${minBreakpointQuery.large`
    gap: 50px;
  `}

  ${minBreakpointQuery.xxlarge`
    gap: 60px;
  `}
`;

const StyledItem = styled.div``;

const StyledSubheading = styled.h3`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
  font-weight: ${fontWeights.medium};
  ${fontSize(19)};

  ${minBreakpointQuery.small`
    gap: 30px;
    margin-bottom: 30px;
    ${fontSize(22)};
  `}

  ${minBreakpointQuery.mlarge`
    white-space: nowrap;
  `}

  ${minBreakpointQuery.large`
    gap: 40px;
    margin-bottom: 40px;
    ${fontSize(25)};
  `}

  &:after {
    content: '';
    position: relative;
    display: block;
    height: 1px;
    width: 100%;
    background-color: ${standardColours.darkGrey};
  }
`;

const StyledProfiles = styled.div`
  display: grid;
  gap: 30px 20px;

  ${minBreakpointQuery.small`
    row-gap: 40px;
  `}

  ${minBreakpointQuery.large`
    row-gap: 50px;
  `}

  ${minBreakpointQuery.xxlarge`
    row-gap: 60px;
  `}

  ${({ isVerticalLayout }) => {
    if (!isVerticalLayout) {
      return css`
        ${minBreakpointQuery.tiny`
          grid-template-columns: repeat(2, 1fr);
        `}

        ${minBreakpointQuery.tsmall`
          grid-template-columns: repeat(3, 1fr);
        `}

        ${minBreakpointQuery.small`
          grid-template-columns: repeat(4, 1fr);
        `}

        ${minBreakpointQuery.smedium`
          grid-template-columns: repeat(5, 1fr);
        `}

        ${minBreakpointQuery.mlarge`
          grid-template-columns: repeat(6, 1fr);
        `}
      `;
    }
  }};
`;

//moved up for selection
const StyledContent = styled.div`
  ${({ isVerticalLayout }) => {
    if (!isVerticalLayout) {
      return css`
        margin-top: 22px;
      `;
    }
  }};
`;

const StyledProfile = styled.div`
  ${({ isVerticalLayout }) => {
    if (isVerticalLayout) {
      return css`
        display: grid;
        align-items: center;
        gap: 20px;

        ${maxBreakpointQuery.tsmall`
          text-align: center;
        `}

        ${minBreakpointQuery.small`
          gap: 30px;
        `}

        ${minBreakpointQuery.large`
          gap: 40px;
        `}

        ${minBreakpointQuery.xxlarge`
          gap: 50px;
        `}

        &:nth-child(odd) {
          ${minBreakpointQuery.tsmall`
            grid-template-columns: 0.35fr 0.65fr;
          `}

          ${minBreakpointQuery.smedium`
            grid-template-columns: 0.3fr 0.7fr;
          `}

          ${minBreakpointQuery.mlarge`
            grid-template-columns: 0.25fr 0.75fr;
          `}
        }

        &:nth-child(even) {
          ${minBreakpointQuery.tsmall`
            grid-template-columns: 0.65fr 0.35fr;

            ${StyledContent} {
              grid-row: 1;
            }
          `}

          ${minBreakpointQuery.smedium`
            grid-template-columns: 0.7fr 0.3fr;
          `}

          ${minBreakpointQuery.mlarge`
            grid-template-columns: 0.75fr 0.25fr;
          `}
        }
      `;
    } else {
      return css`
        text-align: center;
      `;
    }
  }};
`;

const StyledImage = styled(GatsbyImage)`
  ${({ isVerticalLayout }) => {
    if (isVerticalLayout) {
      return css`
        ${minBreakpointQuery.tsmall`
          height: 100%;
        `}
      `;
    }
  }};
`;

const StyledName = styled.h4`
  font-weight: ${fontWeights.medium};

  ${({ isVerticalLayout }) => {
    if (isVerticalLayout) {
      return css`
        ${fontSize(19)};

        ${minBreakpointQuery.small`
          ${fontSize(22)};
        `}

        ${minBreakpointQuery.large`
          ${fontSize(25)};
        `}
      `;
    } else {
      return css`
        ${fontSize(18)};

        ${minBreakpointQuery.large`
          ${fontSize(20)};
        `}
      `;
    }
  }};
`;

const StyledRole = styled.span`
  display: block;
  margin-top: 8px;

  ${minBreakpointQuery.small`
    margin-top: 11px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 14px;
  `}
`;

const StyledBio = styled.p`
  margin-top: 12px;
  line-height: 1.8;

  ${minBreakpointQuery.small`
    margin-top: 15px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 18px;
  `}
`;

const TeamProfiles = ({ heading, overline, items }) => (
  <StyledTeamProfiles>
    <Container>
      <StyledHeader>
        <StyledOverline>{overline}</StyledOverline>
        <Heading>{heading}</Heading>
      </StyledHeader>
      <StyledItems>
        {items.map(({ name, teamMembers, isVerticalLayout }, id) => (
          <StyledItem key={id}>
            <StyledSubheading>{name}</StyledSubheading>
            <StyledProfiles isVerticalLayout={isVerticalLayout}>
              {teamMembers.map(({ name, image, role, bio }, id) => (
                <StyledProfile key={id} isVerticalLayout={isVerticalLayout}>
                  <StyledImage
                    image={image.gatsbyImageData}
                    alt={image.alt}
                    isVerticalLayout={isVerticalLayout}
                  />
                  <StyledContent isVerticalLayout={isVerticalLayout}>
                    <StyledName isVerticalLayout={isVerticalLayout}>
                      {name}
                    </StyledName>
                    {role && <StyledRole>{role}</StyledRole>}
                    {bio && <StyledBio>{bio}</StyledBio>}
                  </StyledContent>
                </StyledProfile>
              ))}
            </StyledProfiles>
          </StyledItem>
        ))}
      </StyledItems>
    </Container>
  </StyledTeamProfiles>
);

export default TeamProfiles;
